import {
  CheckCircleRounded,
  Close,
  ConfirmationNumberOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Icon,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { mensajeErrorFiscal } from "services/facturacionErrores";
import PropTypes from "prop-types";
import {
  ESTADO_IMPRESION,
  RANURA,
  estadosIniciales,
  imprimirDocumentosVenta,
  imprimirRanura,
} from "../impresion";
import { leyendaComandaRemota, tituloRanura } from "../impresion/etiquetas";
import { formatCurrency, getCustomerDisplayName, getStockInsuficienteInfo } from "../utils";
import DocumentoImpresionCard from "./DocumentoImpresionCard";

const SIN_ERRORES = { [RANURA.FACTURA]: null, [RANURA.COMANDA]: null, [RANURA.COMANDA_REMOTA]: null };

export default function PaymentFlowDialog({
  open,
  cartItems,
  customer,
  paymentMethod,
  voucher,
  totals,
  onClose,
  onComplete,
  onNewSale,
  onObtenerDocumentos,
  esDemo = false,
}) {
  const [completedSale, setCompletedSale] = useState(null);

  const [procesando, setProcesando] = useState(false);
  const [errorCobro, setErrorCobro] = useState("");
  const [detalleStock, setDetalleStock] = useState(null);

  // Impresión de la venta cerrada. La cola vive fuera del diálogo; acá sólo se
  // refleja el estado de cada papel. El ref identifica la venta actual para que
  // un trabajo de la venta anterior, que puede seguir corriendo, no pise estos
  // estados.
  const [documentos, setDocumentos] = useState(null);
  const [estadosImpresion, setEstadosImpresion] = useState(() => estadosIniciales(null));
  const [erroresImpresion, setErroresImpresion] = useState(SIN_ERRORES);
  const [cargandoDocumentos, setCargandoDocumentos] = useState(false);
  const [errorDocumentos, setErrorDocumentos] = useState("");
  const ventaActualRef = useRef(null);

  useEffect(() => {
    if (open) {
      setCompletedSale(null);
      setProcesando(false);
      setErrorCobro("");
      setDetalleStock(null);
      setDocumentos(null);
      setEstadosImpresion(estadosIniciales(null));
      setErroresImpresion(SIN_ERRORES);
      setCargandoDocumentos(false);
      setErrorDocumentos("");
      ventaActualRef.current = null;
    }
  }, [open]);

  const actualizarEstado = (claveVenta) => (ranura, estado, error = null) => {
    if (ventaActualRef.current !== claveVenta) return;
    setEstadosImpresion((previos) => ({ ...previos, [ranura]: estado }));
    setErroresImpresion((previos) => ({ ...previos, [ranura]: error || null }));
  };

  const adoptarDocumentos = (venta, dto) => {
    const claveVenta = venta?.idPedido ?? venta?.id ?? null;
    ventaActualRef.current = claveVenta;
    setDocumentos(dto);
    const iniciales = estadosIniciales(dto);
    if (esDemo) iniciales[RANURA.COMANDA_REMOTA] = ESTADO_IMPRESION.NO_APLICA;
    setEstadosImpresion(iniciales);
    setErroresImpresion(SIN_ERRORES);
    return claveVenta;
  };

  // Automático sólo con una respuesta nueva: una clave repetida ya pudo haber
  // impreso y volver a hacerlo duplicaría papeles. La promesa no se espera: el
  // cajero puede iniciar la venta siguiente mientras la cola sigue.
  const iniciarImpresionAutomatica = (venta, dto) => {
    const claveVenta = adoptarDocumentos(venta, dto);
    if (!dto || venta?.reutilizada) return;
    imprimirDocumentosVenta(dto, { onEstado: actualizarEstado(claveVenta), omitirComandaRemota: esDemo });
  };

  const handleReimprimir = async (ranura) => {
    if (!documentos) return;
    const claveVenta = ventaActualRef.current;
    const avisar = actualizarEstado(claveVenta);
    avisar(ranura, ESTADO_IMPRESION.PREPARANDO);
    const resultado = await imprimirRanura(documentos, ranura);
    avisar(ranura, resultado.estado, resultado.error);
  };

  // Cuando la mutación cerró la venta pero no pudo armar el documento, se
  // vuelve a pedir por consulta. Nunca autoimprime: es una acción manual.
  const handleCargarDocumentos = async () => {
    if (!completedSale?.idPedido || !onObtenerDocumentos) return;
    setCargandoDocumentos(true);
    setErrorDocumentos("");
    try {
      const dto = await onObtenerDocumentos(completedSale.idPedido);
      adoptarDocumentos(completedSale, dto);
    } catch (error) {
      setErrorDocumentos(error?.message || "No se pudieron recuperar los documentos.");
    } finally {
      setCargandoDocumentos(false);
    }
  };

  const isFullyCovered = Boolean(voucher) && totals.amountDue <= 0.01;
  // Rama exclusiva del mock: QR dummy y pago simulado. El QR real de Cobrand
  // llega en su propia etapa y tiene que construir su propia rama, no reusar
  // esta. El `esDemo` evita que se active sola si un medio real se llama "qr".
  const isQrFlow = esDemo && !isFullyCovered && paymentMethod?.code === "qr";
  const actionLabel = isFullyCovered
    ? "Registrar voucher y confirmar"
    : isQrFlow
      ? "Simular pago aprobado"
      : `Confirmar cobro en ${paymentMethod?.label || "caja"}`;
  // onComplete puede ser sincrónico (demo) o devolver una promesa (venta real).
  // Await sirve para ambos; sin esto la promesa se guardaría como si fuera la venta.
  const handleComplete = async () => {
    if (completedSale || procesando) return;
    setProcesando(true);
    setErrorCobro("");
    setDetalleStock(null);
    try {
      const venta = await onComplete();
      if (venta) {
        setCompletedSale(venta);
        iniciarImpresionAutomatica(venta, venta.documentosImpresion || null);
      }
    } catch (error) {
      const stockInsuficiente = getStockInsuficienteInfo(error);
      setDetalleStock(stockInsuficiente);
      setErrorCobro(
        stockInsuficiente
          ? "No hay stock suficiente para completar esta venta."
          : mensajeErrorFiscal(error, "No se pudo completar el cobro. Consultá el estado de la venta antes de volver a confirmar.")
      );
    } finally {
      setProcesando(false);
    }
  };

  return (
    <Dialog open={open} onClose={completedSale ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        {!completedSale ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.05fr .95fr" }, minHeight: 540 }}>
            <Box sx={{ p: { xs: 2.5, md: 4 }, display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box>
                  {esDemo && (
                    <Chip size="small" label="SIMULACIÓN" sx={{ bgcolor: "#FFF0F7", color: "#B00058", fontWeight: 900 }} />
                  )}
                  <Typography sx={{ fontSize: 27, lineHeight: 1.1, fontWeight: 900, color: "#172033", mt: 1.25 }}>
                    {isFullyCovered ? "Confirmar venta bonificada" : isQrFlow ? "Esperando el pago" : "Confirmar cobro manual"}
                  </Typography>
                  <Typography sx={{ color: "#64748B", fontSize: 14, mt: 0.75 }}>
                    {isFullyCovered
                      ? "El voucher cubre el pedido completo y no requiere un medio de pago."
                      : `Medio seleccionado para el saldo: ${paymentMethod?.label || "Sin definir"}.`}
                  </Typography>
                </Box>
                <IconButton aria-label="Cerrar cobro" onClick={onClose}><Close /></IconButton>
              </Box>

              <Box sx={{ my: 3, p: 2, bgcolor: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "16px" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1 }}>
                  <Typography sx={{ color: "#64748B", fontSize: 13 }}>Cliente</Typography>
                  <Typography sx={{ color: "#172033", fontSize: 13, fontWeight: 800, textAlign: "right" }}>
                    {getCustomerDisplayName(customer)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1 }}>
                  <Typography sx={{ color: "#64748B", fontSize: 13 }}>Unidades</Typography>
                  <Typography sx={{ color: "#172033", fontSize: 13, fontWeight: 800 }}>
                    {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                  </Typography>
                </Box>
                {totals.voucherApplied > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1 }}>
                    <Typography sx={{ color: "#9A6209", fontSize: 13 }}>Voucher físico</Typography>
                    <Typography sx={{ color: "#9A6209", fontSize: 13, fontWeight: 800 }}>
                      − {formatCurrency(totals.voucherApplied)}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", pt: 1, borderTop: "1px solid #E2E8F0" }}>
                  <Typography sx={{ fontWeight: 800, color: "#172033" }}>A cobrar</Typography>
                  <Typography data-testid="payment-amount" sx={{ fontWeight: 900, color: "#2C3087", fontSize: 28 }}>
                    {formatCurrency(totals.amountDue)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: "auto" }}>
                {(isFullyCovered || paymentMethod?.requiresSellerConfirmation) && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1.5, color: "#80520D" }}>
                    <ShieldOutlined sx={{ fontSize: 19, mt: 0.1 }} />
                    <Typography sx={{ fontSize: 12 }}>
                      {isFullyCovered
                        ? "Confirmá solamente después de recibir el voucher físico."
                        : "Confirmá solamente después de verificar el cobro."}
                    </Typography>
                  </Box>
                )}
                {errorCobro && (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 1.25,
                      borderRadius: "10px",
                      bgcolor: "#FDECEA",
                      border: "1px solid #F5C2C0",
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, color: "#B3261E", fontWeight: 700 }}>
                      {errorCobro}
                    </Typography>
                    {detalleStock?.faltantes?.map((faltante) => (
                      <Box key={`${faltante.idMateriaPrima}`} sx={{ mt: 0.75 }}>
                        <Typography sx={{ fontSize: 12.5, color: "#8F1D18", fontWeight: 900 }}>
                          {faltante.materiaPrima}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#B3261E" }}>
                          Disponible: {faltante.disponible} · Requerido: {faltante.requerido} · Faltan: {faltante.faltante}
                        </Typography>
                        {faltante.productos.length > 0 && (
                          <Typography sx={{ fontSize: 11.5, color: "#B3261E" }}>
                            Productos afectados: {faltante.productos.map((producto) => producto.producto).join(", ")}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 1.25 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={procesando}
                    onClick={onClose}
                    sx={{
                      borderRadius: "12px",
                      borderColor: "#2C3087",
                      bgcolor: "#fff",
                      color: "#2C3087",
                      textTransform: "none",
                      fontWeight: 800,
                      "&:hover": { borderColor: "#22266F", bgcolor: "#F3F2FF", color: "#22266F", opacity: 1 },
                    }}
                  >
                    Volver
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    data-testid="confirm-payment"
                    disabled={procesando}
                    onClick={handleComplete}
                    sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 900, bgcolor: "#2C3087", color: "#fff", "&:hover": { bgcolor: "#22266F" } }}
                  >
                    {procesando ? "Procesando…" : actionLabel}
                  </Button>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                p: { xs: 2.5, md: 4 },
                bgcolor: "#20245F",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                textAlign: "center",
              }}
            >
              {isFullyCovered ? (
                <>
                  <Chip label="SIN PAGO ADICIONAL" size="small" sx={{ bgcolor: "rgba(255,255,255,.13)", color: "#fff", fontWeight: 900, mb: 2 }} />
                  <Box sx={{ width: 112, height: 112, borderRadius: "32px", display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,.12)" }}>
                    <ConfirmationNumberOutlined sx={{ fontSize: 58 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 900, fontSize: 27, mt: 2 }}>100% cubierto</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,.78)", fontSize: 13, maxWidth: 280 }}>
                    {voucher?.name}. El cajero registra la recepción del voucher; el cliente no interactúa con el sistema.
                  </Typography>
                </>
              ) : isQrFlow ? (
                <>
                  <Chip label="MONITOR SECUNDARIO · SOLO LECTURA" size="small" sx={{ bgcolor: "rgba(255,255,255,.13)", color: "#fff", fontWeight: 900, mb: 2 }} />
                  <Box sx={{ bgcolor: "#fff", p: 2, borderRadius: "18px", display: "grid", placeItems: "center" }}>
                    <QRCode
                      value={`upro-demo://evento/${paymentMethod?.code || "qr"}/${totals.amountDue}`}
                      size={190}
                      fgColor="#20245F"
                    />
                  </Box>
                  <Typography sx={{ fontWeight: 900, fontSize: 29, mt: 2 }}>{formatCurrency(totals.amountDue)}</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,.72)", fontSize: 13, maxWidth: 280 }}>
                    El cajero presenta este código para que el cliente lo escanee con su teléfono. El cliente no opera el sistema y aquí no se muestran DNI ni datos internos.
                  </Typography>
                </>
              ) : (
                <>
                  <Box sx={{ width: 96, height: 96, borderRadius: "28px", display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,.12)" }}>
                    <Icon sx={{ fontSize: 50 }}>{paymentMethod?.icon || "payments"}</Icon>
                  </Box>
                  <Typography sx={{ fontWeight: 900, fontSize: 25, mt: 2 }}>{paymentMethod?.label}</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,.72)", fontSize: 13, maxWidth: 280 }}>
                    Esta operación depende de la confirmación del vendedor y no consulta ningún servicio real.
                  </Typography>
                  {voucher && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2, p: 1.25, borderRadius: "12px", bgcolor: "rgba(255,255,255,.1)" }}>
                      <ConfirmationNumberOutlined />
                      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{voucher.name}</Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        ) : (
          <Box data-testid="payment-success" sx={{ p: { xs: 3, md: 6 }, textAlign: "center" }}>
            <CheckCircleRounded sx={{ color: "#2C9A83", fontSize: 78 }} />
            <Chip size="small" label={isFullyCovered ? "VENTA REGISTRADA" : "PAGO APROBADO"} sx={{ display: "flex", width: "fit-content", mx: "auto", mt: 1, bgcolor: "#E8F7F2", color: "#18735F", fontWeight: 900 }} />
            <Typography sx={{ fontSize: 30, fontWeight: 900, color: "#172033", mt: 1.5 }}>Venta {completedSale.id}</Typography>
            <Typography sx={{ color: "#64748B", fontSize: 14, mt: 0.5 }}>
              {esDemo
                ? "La venta dummy quedó agregada a “Ventas recientes”."
                : "La venta quedó registrada y aparece en “Ventas recientes”."}
            </Typography>
            {isFullyCovered && (
              <Chip
                size="small"
                icon={<ConfirmationNumberOutlined sx={{ fontSize: 16 }} />}
                label="Recepción del voucher físico registrada"
                sx={{ mt: 1.5, bgcolor: "#FFF5E5", color: "#9A6209", fontWeight: 800 }}
              />
            )}
            {completedSale.reutilizada && (
              <Box sx={{ mt: 2, mx: "auto", maxWidth: 520, p: 1.5, borderRadius: "12px", bgcolor: "#FFF5E5", border: "1px solid #F3D9A4", textAlign: "left" }}>
                <Typography sx={{ fontSize: 12.5, color: "#80520D", fontWeight: 700 }}>
                  Esta venta ya existía: se recuperó la operación original y no se imprimió de nuevo para no duplicar papeles. Si hace falta, reimprimí cada documento.
                </Typography>
              </Box>
            )}
            {!documentos && !esDemo && (
              <Box sx={{ mt: 2, mx: "auto", maxWidth: 520, p: 1.5, borderRadius: "12px", bgcolor: "#FDECEA", border: "1px solid #F5C2C0", textAlign: "left" }}>
                <Typography sx={{ fontSize: 12.5, color: "#B3261E", fontWeight: 700 }}>
                  {errorDocumentos || "No se pudieron preparar los documentos de impresión. La venta quedó registrada."}
                </Typography>
                {onObtenerDocumentos && completedSale.idPedido && (
                  <Button
                    size="small"
                    variant="text"
                    disabled={cargandoDocumentos}
                    onClick={handleCargarDocumentos}
                    sx={{ mt: 0.5, color: "#B3261E", textTransform: "none", fontWeight: 800, px: 0 }}
                  >
                    {cargandoDocumentos ? "Recuperando…" : "Recuperar documentos"}
                  </Button>
                )}
              </Box>
            )}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, maxWidth: 560, mx: "auto", mt: 3, mb: leyendaComandaRemota(estadosImpresion[RANURA.COMANDA_REMOTA]) ? 1 : 3 }}>
              <DocumentoImpresionCard
                testId="impresion-factura"
                titulo={tituloRanura(RANURA.FACTURA, documentos)}
                estado={estadosImpresion[RANURA.FACTURA]}
                error={erroresImpresion[RANURA.FACTURA]}
                icono="factura"
                onImprimir={documentos ? () => handleReimprimir(RANURA.FACTURA) : undefined}
              />
              <DocumentoImpresionCard
                testId="impresion-comanda"
                titulo={tituloRanura(RANURA.COMANDA, documentos)}
                estado={estadosImpresion[RANURA.COMANDA]}
                error={erroresImpresion[RANURA.COMANDA]}
                icono="comanda"
                onImprimir={documentos ? () => handleReimprimir(RANURA.COMANDA) : undefined}
              />
            </Box>
            {leyendaComandaRemota(estadosImpresion[RANURA.COMANDA_REMOTA]) && (
              <Typography
                data-testid="impresion-comanda-remota"
                data-estado={estadosImpresion[RANURA.COMANDA_REMOTA]}
                title={erroresImpresion[RANURA.COMANDA_REMOTA] || undefined}
                sx={{ mb: 3, fontSize: 11.5, color: "#94A3B8" }}
              >
                {leyendaComandaRemota(estadosImpresion[RANURA.COMANDA_REMOTA])}
              </Typography>
            )}
            <Button
              variant="contained"
              size="large"
              data-testid="new-sale-button"
              onClick={onNewSale}
              sx={{ bgcolor: "#2C3087", color: "#fff", borderRadius: "12px", textTransform: "none", fontWeight: 900, px: 4, "&:hover": { bgcolor: "#22266F" } }}
            >
              Iniciar nueva venta
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

PaymentFlowDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  cartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  customer: PropTypes.object,
  paymentMethod: PropTypes.object,
  voucher: PropTypes.object,
  totals: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onNewSale: PropTypes.func.isRequired,
  onObtenerDocumentos: PropTypes.func,
  esDemo: PropTypes.bool,
};
